import React, { useState } from 'react';
import { Button, Card, Alert, Form, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Formik } from 'formik';
import * as Yup from 'yup';

// Validation schema
const validationSchema = Yup.object({
  name: Yup.string()
    .required('Full name is required')
    .min(2, 'Name must be at least 2 characters'),
  email: Yup.string()
    .required('Email is required')
    .email('Please enter a valid email address'),
  password: Yup.string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
  certificationLevel: Yup.string()
    .required('Please select your certification level'),
  numberOfDives: Yup.number()
    .required('Number of dives is required')
    .min(0, 'Number of dives cannot be negative')
    .integer('Number of dives must be a whole number'),
  emergencyContact: Yup.object({
    name: Yup.string()
      .required('Emergency contact name is required'),
    relationship: Yup.string()
      .required('Relationship is required'),
    phone: Yup.string()
      .required('Emergency contact phone is required')
      .matches(/^[\d+\-\s()]+$/, 'Phone number can only contain digits, +, -, spaces, and parentheses')
      .min(7, 'Phone number is too short')
  })
});

const RegisterForm = () => {
  const [apiError, setApiError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  const initialValues = {
    name: '',
    email: '',
    password: '',
    certificationLevel: '',
    numberOfDives: 0,
    emergencyContact: {
      name: '',
      relationship: '',
      phone: ''
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setApiError(null);
    setSuccessMessage(null);
    
    try {
      const response = await register(values);
      
      // Scroll to top so user sees the success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Show success message
      setSuccessMessage(response.message || 'Registration successful! Please check your email to verify your account.');
      
      // Redirect to login page after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      console.error("Registration error:", error);
      setApiError(error.response?.data?.message || 'Registration failed. Please try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="p-4 shadow">
      <Card.Body>
        <h2 className="text-center mb-4">Register</h2>
        
        {apiError && <Alert variant="danger">{apiError}</Alert>}
        {successMessage && (
          <Alert variant="success">
            <Alert.Heading>Success!</Alert.Heading>
            {successMessage}
            <hr />
            <p className="mb-0">
              <small>Redirecting to login page in 3 seconds...</small>
            </p>
          </Alert>
        )}
        
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Full Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.name && errors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Email *</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.email && errors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Password *</Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.password && errors.password}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.password}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Must be at least 6 characters
                </Form.Text>
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Certification Level *</Form.Label>
                    <Form.Select
                      name="certificationLevel"
                      value={values.certificationLevel}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.certificationLevel && errors.certificationLevel}
                    >
                      <option value="">Select Level</option>
                      <option value="OW">Open Water</option>
                      <option value="AOW">Advanced Open Water</option>
                      <option value="Rescue">Rescue Diver</option>
                      <option value="DM">Divemaster</option>
                      <option value="Instructor">Instructor</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.certificationLevel}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Number of Dives *</Form.Label>
                    <Form.Control
                      type="number"
                      name="numberOfDives"
                      value={values.numberOfDives}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.numberOfDives && errors.numberOfDives}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.numberOfDives}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <h5 className="mt-3">Emergency Contact</h5>
              
              <Form.Group className="mb-3">
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="emergencyContact.name"
                  value={values.emergencyContact.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.emergencyContact?.name && errors.emergencyContact?.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.emergencyContact?.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Relationship *</Form.Label>
                <Form.Control
                  type="text"
                  name="emergencyContact.relationship"
                  value={values.emergencyContact.relationship}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="e.g., Spouse, Parent, Friend"
                  isInvalid={touched.emergencyContact?.relationship && errors.emergencyContact?.relationship}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.emergencyContact?.relationship}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Phone *</Form.Label>
                <Form.Control
                  type="tel"
                  name="emergencyContact.phone"
                  value={values.emergencyContact.phone}
                  onChange={(e) => {
                    // Sanitize phone input - only allow valid phone characters
                    const sanitized = e.target.value.replace(/[^\d+\-\s()]/g, '');
                    handleChange({
                      target: {
                        name: e.target.name,
                        value: sanitized
                      }
                    });
                  }}
                  onBlur={handleBlur}
                  placeholder="+1 (555) 123-4567"
                  isInvalid={touched.emergencyContact?.phone && errors.emergencyContact?.phone}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.emergencyContact?.phone}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  International format accepted (e.g., +1 555-123-4567, +49 30 12345678)
                </Form.Text>
              </Form.Group>

              <Button 
                type="submit" 
                variant="primary" 
                className="w-100 mt-3" 
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Registering...' : 'Register'}
              </Button>
            </Form>
          )}
        </Formik>
      </Card.Body>
    </Card>
  );
};

export default RegisterForm;
